import { useEffect, type ReactNode } from 'react';
import { ErreurApi } from '../api/client.ts';
import { Icone, type NomIcone } from './icones.tsx';

/** Éléments d'interface partagés par tous les écrans. */

/**
 * Fenêtre modale — une fiche ponctuelle (prix, seuil, boutiques, photos…)
 * par-dessus la liste, sans y naviguer.
 *
 * Le contenu garde son propre bouton « Annuler/Fermer » (`onFerme` des
 * formulaires existants) : la modale ajoute juste le FOND cliquable et la
 * touche Échap, elle ne remplace rien.
 */
export function Modale({
  onFermer,
  taille = 'normal',
  children,
}: {
  onFermer: () => void;
  /** `large` : pour un contenu qui a besoin de place (un aperçu PDF). */
  taille?: 'normal' | 'large';
  children: ReactNode;
}): ReactNode {
  useEffect(() => {
    function surTouche(evenement: KeyboardEvent): void {
      if (evenement.key === 'Escape') {
        onFermer();
      }
    }
    window.addEventListener('keydown', surTouche);
    return () => {
      window.removeEventListener('keydown', surTouche);
    };
  }, [onFermer]);

  return (
    <div
      className="modale-fond"
      onClick={onFermer}
      role="presentation"
    >
      <div
        className={taille === 'large' ? 'modale modale--large' : 'modale'}
        role="dialog"
        aria-modal="true"
        onClick={(evenement) => {
          // Sans ça, un clic DANS la fiche remonterait au fond et la
          // refermerait — personne ne peut alors cliquer un champ.
          evenement.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Montant en FCFA — RG-001.
 *
 * Toujours entier, jamais de décimale. L'espace insécable fine sépare les
 * milliers : au comptoir comme au bureau, un chiffre mal groupé se lit de
 * travers, et 1500000 n'est pas lisible.
 */
export function Montant({ valeur }: { valeur: unknown }): ReactNode {
  const nombre = Number(valeur ?? 0);
  const affichage = Number.isFinite(nombre)
    ? nombre.toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ')
    : '—';
  return <span className="montant">{affichage} F</span>;
}

export function Nombre({ valeur }: { valeur: unknown }): ReactNode {
  const nombre = Number(valeur ?? 0);
  return (
    <span className="montant">
      {Number.isFinite(nombre) ? nombre.toLocaleString('fr-FR') : '—'}
    </span>
  );
}

/** Version TEXTE BRUT de `<DateHeure>` — pour l'export PDF. */
export function formaterDateHeure(valeur: unknown): string {
  if (typeof valeur !== 'string' && !(valeur instanceof Date)) {
    return '—';
  }
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
    // Stockage en UTC, affichage en heure locale — RG-006.
    timeZone: 'Africa/Porto-Novo',
  });
}

export function DateHeure({ valeur }: { valeur: unknown }): ReactNode {
  return <span className="mono">{formaterDateHeure(valeur)}</span>;
}

/**
 * Erreur d'API.
 *
 * On affiche le message du serveur ET la règle qui l'a produit. Le code de
 * règle n'est pas décoratif : il permet à l'utilisateur de citer `RG-057`
 * plutôt que « ça ne marche pas », et de retrouver la spécification.
 */
export function Erreur({ erreur }: { erreur: unknown }): ReactNode {
  if (erreur === null || erreur === undefined) {
    return null;
  }

  if (erreur instanceof ErreurApi) {
    return (
      <div className="message message--erreur" role="alert">
        {erreur.message}
        {erreur.regle !== undefined && (
          <div className="message--regle">Règle {erreur.regle}</div>
        )}
      </div>
    );
  }

  return (
    <div className="message message--erreur" role="alert">
      {erreur instanceof Error ? erreur.message : 'Erreur inattendue.'}
    </div>
  );
}

/**
 * Avertissements renvoyés par l'API.
 *
 * Distincts des erreurs, et c'est tout l'intérêt : l'opération a RÉUSSI. Un
 * ordre de prix incohérent (RG-037) ou un produit sans prix (RG-029) se
 * signalent sans bloquer.
 */
export function Avertissements({
  liste,
}: {
  liste: { code: string; message: string }[] | undefined;
}): ReactNode {
  if (liste === undefined || liste.length === 0) {
    return null;
  }
  return (
    <div className="message message--attention" role="status">
      {liste.map((avertissement) => (
        <div key={avertissement.code}>{avertissement.message}</div>
      ))}
    </div>
  );
}

export function Carte({
  titre,
  icone,
  actions,
  children,
}: {
  titre?: string;
  /**
   * Bandeau d'en-tête plein, icône + titre en clair — pour une carte qui
   * délimite une SECTION d'un long formulaire (identité, prix, stock…).
   *
   * Réservé à ce cas : une carte de contenu ordinaire (tableau, fiche)
   * garde son titre discret, comme avant. Un bandeau sur chaque carte de
   * l'écran ferait de la couleur un simple décor, plus un repère.
   */
  icone?: NomIcone;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  if (icone !== undefined && titre !== undefined) {
    return (
      <section className="carte carte--bandeau">
        <div className="carte__bandeau">
          <Icone nom={icone} taille={17} />
          <h2>{titre}</h2>
          {actions}
        </div>
        <div className="carte__corps">{children}</div>
      </section>
    );
  }

  return (
    <section className="carte">
      {(titre !== undefined || actions !== undefined) && (
        <div className="entete" style={{ marginBottom: 12 }}>
          {titre !== undefined && <h2>{titre}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Indicateur({
  libelle,
  valeur,
  note,
}: {
  libelle: string;
  valeur: ReactNode;
  note?: string;
}): ReactNode {
  return (
    <div className="carte">
      <div className="indicateur__libelle">{libelle}</div>
      <div className="indicateur__valeur">{valeur}</div>
      {note !== undefined && <div className="indicateur__note">{note}</div>}
    </div>
  );
}

export function Vide({ texte }: { texte: string }): ReactNode {
  return <div className="vide">{texte}</div>;
}

export function Chargement(): ReactNode {
  return <div className="vide">Chargement…</div>;
}

export function Etiquette({
  texte,
  ton,
}: {
  texte: string;
  ton?: 'vert' | 'rouge' | 'ambre';
}): ReactNode {
  const classe = ton === undefined ? 'etiquette' : `etiquette etiquette--${ton}`;
  return <span className={classe}>{texte}</span>;
}
